import type { ReactNode } from 'react'

import { cn } from '@/components/ui'
import { parseBlocks, splitOnMarkers } from '@/letterMarkdown'

/**
 * A draft, shown as the letter it is rather than as the markdown it was typed in.
 *
 * `draft_v5` writes `**bold**`, `## headings`, `- bullets` and a pipe table, and
 * the screens used to print all of it inside a monospace `<pre>` — asterisks and
 * all (TM-04). This draws those as what they say they are. It is deliberately
 * small: no library, nothing it does not need to recognise, and anything it does
 * not recognise is shown as the text it is.
 *
 * ## What it must not do
 *
 * It must not lose, reword or reorder a word, and above all it must not touch an
 * `[ATTORNEY: …]` marker: a marker is the drafter asking a person to decide
 * something. Markers are found first and are atomic — formatting characters
 * around or inside one never swallow it — and each is handed to `renderMarker`
 * (the Workbench uses it to register a jump target), falling back to a plain
 * highlight.
 */

export type MarkerRenderer = (marker: string, key: string) => ReactNode

function defaultMarker(marker: string, key: string): ReactNode {
  return (
    <mark key={key} className="rounded bg-warn-bg px-0.5 text-warn-fg">
      {marker}
    </mark>
  )
}

// Conservative on purpose: an asterisk or underscore inside a word, a bullet
// or a lone `*` is left alone. Same shapes the .docx renderer recognises
// (`server/app/review/render_docx.py`), so a draft reads alike on screen and
// in the file.
const INLINE =
  /\*\*(?<b>(?=\S).+?(?<=\S))\*\*|(?<![\w*])\*(?<i>[^\s*](?:.*?[^\s*])?)\*(?![\w*])|(?<!\w)_(?<i2>[^\s_](?:.*?[^\s_])?)_(?!\w)|`(?<c>[^`]+)`|\[(?<lt>(?!ATTORNEY:)[^\]]+)\]\((?<lu>https?:\/\/[^)\s]+)\)/g

function renderFormatted(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let cursor = 0
  let n = 0
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0
    if (at > cursor) nodes.push(text.slice(cursor, at))
    const key = `${keyPrefix}-${n++}`
    const g = match.groups ?? {}
    if (g.b !== undefined) nodes.push(<strong key={key}>{g.b}</strong>)
    else if (g.i !== undefined) nodes.push(<em key={key}>{g.i}</em>)
    else if (g.i2 !== undefined) nodes.push(<em key={key}>{g.i2}</em>)
    else if (g.c !== undefined) nodes.push(g.c)
    else
      nodes.push(
        <a key={key} href={g.lu} target="_blank" rel="noopener noreferrer" className="underline">
          {g.lt}
        </a>,
      )
    cursor = at + match[0].length
  }
  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

/**
 * One line of markdown with markers kept whole.
 *
 * Markers are split out first and everything between them is formatted on its
 * own, so `**[ATTORNEY: x]**` shows the marker once with a stray pair of
 * asterisks removed by the formatter rather than the marker being eaten by it.
 */
function renderInline(text: string, keyPrefix: string, renderMarker: MarkerRenderer): ReactNode[] {
  return splitOnMarkers(text).flatMap((segment, index) =>
    segment.marker
      ? [renderMarker(segment.text, `${keyPrefix}-m${index}`)]
      : renderFormatted(segment.text, `${keyPrefix}-f${index}`),
  )
}

export function LetterText({
  text,
  renderMarker = defaultMarker,
  className,
}: {
  text: string
  /** How an `[ATTORNEY: …]` marker is drawn. Default: a highlight. */
  renderMarker?: MarkerRenderer
  className?: string
}) {
  const blocks = parseBlocks(text)

  return (
    <div className={cn('flex flex-col gap-3 text-body leading-relaxed', className)}>
      {blocks.map((block, b) => {
        const key = `b${b}`
        switch (block.kind) {
          case 'heading':
            return (
              <p key={key} className="m-0 font-semibold">
                {renderInline(block.text, key, renderMarker)}
              </p>
            )
          case 'bullets':
            return (
              <ul key={key} className="m-0 flex list-disc flex-col gap-1 pl-5">
                {block.items.map((item, i) => (
                  <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`, renderMarker)}</li>
                ))}
              </ul>
            )
          case 'numbered':
            return (
              <ol key={key} className="m-0 flex list-none flex-col gap-1 pl-4">
                {block.items.map((item, i) => (
                  <li key={`${key}-${i}`}>
                    {item.num} {renderInline(item.text, `${key}-${i}`, renderMarker)}
                  </li>
                ))}
              </ol>
            )
          case 'quote':
            return (
              <blockquote key={key} className="m-0 border-l-2 border-divider pl-3 text-muted">
                {block.lines.map((line, i) => (
                  <span key={`${key}-${i}`} className="block">
                    {renderInline(line, `${key}-${i}`, renderMarker)}
                  </span>
                ))}
              </blockquote>
            )
          case 'table': {
            const [head, ...body] = block.rows
            return (
              <div key={key} className="overflow-x-auto">
                <table className="w-full border-collapse text-meta">
                  <thead>
                    <tr>
                      {(head ?? []).map((cell, c) => (
                        <th
                          key={`${key}-h${c}`}
                          className="border border-divider bg-ground px-2 py-1 text-left font-semibold"
                        >
                          {renderInline(cell, `${key}-h${c}`, renderMarker)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {body.map((row, r) => (
                      <tr key={`${key}-r${r}`}>
                        {row.map((cell, c) => (
                          <td
                            key={`${key}-r${r}c${c}`}
                            className="border border-divider px-2 py-1 align-top"
                          >
                            {renderInline(cell, `${key}-r${r}c${c}`, renderMarker)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
          default:
            // Plain lines stay lines: a letterhead is several of them.
            return (
              <p key={key} className="m-0">
                {block.lines.map((line, i) => (
                  <span key={`${key}-${i}`} className="block">
                    {renderInline(line, `${key}-${i}`, renderMarker)}
                  </span>
                ))}
              </p>
            )
        }
      })}
    </div>
  )
}
